import { useState, useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { RadioGroup, Tooltip, Toggle } from '@ets/design-system';
import cx from 'classnames';

import { useEffectOnUpdate } from '_common/hooks';

import TabStopField, { TempTabStop } from '../TabStopField/TabStopField';

import styles from './CustomTabStop.module.scss';

export type TempCustomTabStop = {
  id: string;
  measure?: Editor.Tabulations.Measure;
  value?: Editor.Tabulations.CustomTabStop['value'];
  hasError?: boolean;
  type: Exclude<Editor.Tabulations.CustomTabStop['type'], 'h'>;
  leader: Editor.Tabulations.CustomTabStop['leader'];
};

type CustomTabStopProps = TempCustomTabStop & {
  onChange: (data: TempCustomTabStop) => void;
  onRemove: (id: string) => void;
  onCollapse: (data: TempCustomTabStop) => void;
  testId: string;
};

const CustomTabStop = ({
  id,
  value,
  measure,
  type,
  leader,
  onChange,
  onRemove,
  onCollapse,
  testId,
}: CustomTabStopProps) => {
  const intl = useIntl();

  const ALIGNMENT_LABEL: { [x in TempCustomTabStop['type']]: string } = {
    l: intl.formatMessage({ id: 'LEFT' }),
    c: intl.formatMessage({ id: 'CENTER' }),
    b: intl.formatMessage({ id: 'BAR_TAB' }),
    r: intl.formatMessage({ id: 'RIGHT' }),
    d: intl.formatMessage({ id: 'DECIMAL_LABEL' }),
    cl: '',
  };

  const LEADER_LABEL: { [x in Exclude<TempCustomTabStop['leader'], undefined>]: string } = {
    n: intl.formatMessage({ id: 'NONE' }),
    d: '.........',
    h: '-------',
    u: '________',
  };

  const [collapsed, setCollapsed] = useState(true);
  const [tempValue, setTempValue] = useState<TempCustomTabStop['value']>(value);
  const [tempMeasure, setTempMeasure] = useState<TempCustomTabStop['measure']>(measure);
  const [tempAlignment, setTempAlignment] = useState<TempCustomTabStop['type']>(type);
  const [tempLeader, setTempLeader] = useState<TempCustomTabStop['leader']>(leader || 'n');
  const [hasError, setHasError] = useState<boolean | undefined>(false);

  useEffect(() => {
    onChange({
      id,
      value: tempValue,
      measure: tempMeasure,
      type: tempAlignment,
      leader: tempLeader,
      hasError: hasError,
    });
  }, [tempValue, tempMeasure, tempAlignment, tempLeader, hasError]);

  useEffectOnUpdate(() => {
    if (collapsed) {
      onCollapse({
        id,
        value: tempValue,
        measure: tempMeasure,
        type: tempAlignment,
        leader: tempLeader,
      });
    }
  }, [collapsed]);

  const toggleCollapsed = () => {
    setCollapsed(!collapsed);
  };

  const removeCustomTabStop = () => {
    onRemove(id);
  };

  const handleAlignmentChange = (alignment: TempCustomTabStop['type']) => {
    setTempAlignment(alignment);
  };

  const handleLeaderChange = (leader: TempCustomTabStop['leader']) => {
    setTempLeader(leader);
  };

  const handleTabStopChange = (data: TempTabStop) => {
    setTempValue(data.value !== undefined && data.value !== '' ? +data.value : undefined);
    setTempMeasure(data.measure);
    setHasError(data.hasError);
  };

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <div className={styles.section}>
          <Toggle
            size="m"
            variant="link"
            isToggled={!collapsed}
            iconProps={{ icon: collapsed ? 'chevron-right' : 'chevron-down' }}
            style={{ margin: '0 1rem 0 0' }}
            onClick={toggleCollapsed}
            testId={`${testId}-collapse`}
          />

          <div
            className={cx({ [styles.empty]: tempValue == null })}
            data-testid={`${testId}-measure`}
          >
            {tempValue != null && tempMeasure ? (
              <>
                <span data-testid={`${testId}-value`}>{`${tempValue}`}</span>{' '}
                <span data-testid={`${testId}-measure`}>{`${tempMeasure}`}</span>
              </>
            ) : (
              intl.formatMessage({ id: 'NO_VALUE_SET' })
            )}
          </div>
        </div>
        <div className={styles.section}>
          <div className={styles.properties}>
            <div className={styles.property}>{ALIGNMENT_LABEL[tempAlignment]}</div>
            <div className={styles.property}> {LEADER_LABEL[tempLeader ?? 'n']}</div>
          </div>
          <Tooltip
            placement="top"
            content={intl.formatMessage({ id: 'DELETE_CUSTOM_TAB_STOP' })}
            testId={`${testId}-delete`}
          >
            <Toggle
              size="m"
              variant="link"
              iconProps={{ icon: 'xmark' }}
              style={{ margin: '0 0 0 2rem' }}
              onClick={removeCustomTabStop}
              testId={`${testId}-delete`}
            />
          </Tooltip>
        </div>
      </div>
      {!collapsed && (
        <div className={styles.body}>
          <TabStopField
            initialValue={value}
            initialMeasure={measure}
            fieldLabel={intl.formatMessage({ id: 'TAB_STOP_VALUE' })}
            selectWidth="14rem"
            onChange={handleTabStopChange}
            testId={`${testId}`}
          />
          <div className={styles.properties}>
            <div className={styles.section}>
              <FormattedMessage id="editor.menu.format.alignment" />
              <RadioGroup
                items={[
                  { value: 'l', label: ALIGNMENT_LABEL.l },
                  { value: 'c', label: ALIGNMENT_LABEL.c },
                  { value: 'd', label: ALIGNMENT_LABEL.d },
                  { value: 'b', label: ALIGNMENT_LABEL.b },
                  { value: 'r', label: ALIGNMENT_LABEL.r },
                ]}
                value={tempAlignment}
                onChange={(e) => handleAlignmentChange(e.target.value)}
                testId={`${testId}-alignment`}
                direction="vertical"
                size="xs"
                className={styles.alignmentRadioGroup}
              />
            </div>
            <div className={styles.section}>
              <FormattedMessage id="LEADER" />
              <RadioGroup
                items={[
                  { value: 'n', label: LEADER_LABEL.n },
                  { value: 'd', label: LEADER_LABEL.d },
                  { value: 'h', label: LEADER_LABEL.h },
                  { value: 'u', label: LEADER_LABEL.u },
                ]}
                value={tempLeader}
                onChange={(e) => handleLeaderChange(e.target.value)}
                testId={`${testId}-leader`}
                direction="vertical"
                size="xs"
                className={styles.leaderRadioGroup}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomTabStop;
